import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Search, Filter, Dumbbell, Play, Image as ImageIcon } from 'lucide-react';
import * as planService from '../../../services/planService';
import ExerciseDetail from './ExerciseDetail';

/**
 * Exercise Library Component
 * Filterable exercise browser with search functionality
 */
export default function ExerciseLibrary({ planId }) {
  const [exercises, setExercises] = useState([]);
  const [filteredExercises, setFilteredExercises] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedExercise, setSelectedExercise] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [muscleGroupFilter, setMuscleGroupFilter] = useState('all');
  const [equipmentFilter, setEquipmentFilter] = useState('all');

  useEffect(() => {
    if (planId) {
      loadExercises();
    }
  }, [planId]);

  useEffect(() => {
    filterExercises();
  }, [exercises, searchTerm, muscleGroupFilter, equipmentFilter]);

  const loadExercises = async () => {
    setIsLoading(true);
    try {
      const result = await planService.getExercises(planId);
      if (result.success) {
        setExercises(result.data || []);
      }
    } catch (error) {
      console.error('Error loading exercises:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const filterExercises = () => {
    let filtered = [...exercises];

    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (e) =>
          e.exercise_name?.toLowerCase().includes(term) ||
          e.description?.toLowerCase().includes(term)
      );
    }

    // Filter by muscle group
    if (muscleGroupFilter !== 'all') {
      filtered = filtered.filter((e) => e.muscle_groups?.includes(muscleGroupFilter));
    }

    // Filter by equipment
    if (equipmentFilter !== 'all') {
      filtered = filtered.filter((e) => e.equipment_type === equipmentFilter);
    }

    setFilteredExercises(filtered);
  };

  const muscleGroups = [
    'chest',
    'back',
    'shoulders',
    'arms',
    'legs',
    'glutes',
    'core',
    'cardio',
    'full_body',
  ];

  const equipmentTypes = [
    'bodyweight',
    'dumbbells',
    'barbell',
    'machine',
    'cable',
    'kettlebell',
    'resistance_bands',
    'cardio',
  ];

  if (selectedExercise) {
    return (
      <ExerciseDetail
        exerciseId={selectedExercise}
        onBack={() => setSelectedExercise(null)}
      />
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading exercises...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Exercise Library</h2>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-xl shadow-lg p-6 space-y-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search exercises..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Filter className="w-4 h-4 inline mr-1" />
              Muscle Group
            </label>
            <select
              value={muscleGroupFilter}
              onChange={(e) => setMuscleGroupFilter(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="all">All Muscle Groups</option>
              {muscleGroups.map((group) => (
                <option key={group} value={group}>
                  {group.charAt(0).toUpperCase() + group.slice(1).replace('_', ' ')}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Filter className="w-4 h-4 inline mr-1" />
              Equipment
            </label>
            <select
              value={equipmentFilter}
              onChange={(e) => setEquipmentFilter(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="all">All Equipment</option>
              {equipmentTypes.map((type) => (
                <option key={type} value={type}>
                  {type.charAt(0).toUpperCase() + type.slice(1).replace('_', ' ')}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Exercise Grid */}
      {filteredExercises.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredExercises.map((exercise) => (
            <motion.div
              key={exercise.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow cursor-pointer"
              onClick={() => setSelectedExercise(exercise.id)}
            >
              {/* Exercise Image/Video Thumbnail */}
              <div className="h-48 bg-gray-100 relative">
                {exercise.video_url ? (
                  <div className="absolute inset-0 flex items-center justify-center bg-gray-200">
                    <Play className="w-12 h-12 text-gray-400" />
                  </div>
                ) : exercise.image_urls && exercise.image_urls.length > 0 ? (
                  <div className="absolute inset-0 flex items-center justify-center bg-gray-200">
                    <ImageIcon className="w-12 h-12 text-gray-400" />
                  </div>
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <Dumbbell className="w-16 h-16 text-gray-300" />
                  </div>
                )}
              </div>

              <div className="p-4">
                <h3 className="text-lg font-bold text-gray-900 mb-2">{exercise.exercise_name}</h3>
                {exercise.description && (
                  <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                    {exercise.description}
                  </p>
                )}
                <div className="flex flex-wrap gap-2 mb-3">
                  {exercise.muscle_groups?.slice(0, 3).map((group, idx) => (
                    <span
                      key={idx}
                      className="px-2 py-1 bg-primary-100 text-primary-800 text-xs rounded-full"
                    >
                      {group}
                    </span>
                  ))}
                </div>
                {exercise.default_sets && exercise.default_reps && (
                  <div className="text-sm text-gray-600">
                    <span className="font-medium">
                      {exercise.default_sets} sets × {exercise.default_reps} reps
                    </span>
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-lg p-12 text-center">
          <Dumbbell className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-900 mb-2">No exercises found</h3>
          <p className="text-gray-600">
            {searchTerm || muscleGroupFilter !== 'all' || equipmentFilter !== 'all'
              ? 'Try adjusting your filters'
              : 'No exercises available for this plan.'}
          </p>
        </div>
      )}
    </div>
  );
}
